import React, { useCallback, useEffect, useState } from "react";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { SelectComponent as Select } from "screens/perfilAutonomo/components/SelectComponent";
import SelectComponent from "components/selectAutoCompleteG";
import { DatePicker } from "screens/perfilAutonomo/components/DatePicker";
import { SecondaryButton } from "screens/perfilAutonomo/components/secondaryButton.component";
import { Loading } from "screens/perfilAutonomo/components/Loading";
import { ExcelIcon } from "assets/iconsSVG/Excel";
import { debounce, FileUploadI } from "screens/perfilAutonomo/utils";
import { toast } from "react-toastify";
import { defaultUGData } from "./utils";
import * as S from "./style";
import { fetchRevendaEmpreendimentosListar } from "../unidades/api/postRevendaEmpreendimentoListar";
import { fetchRevendaAreaUtil } from "../unidades/api/getRevendaAreaUtil";
import { postListarCidadeRevenda } from "./api/postListarCidadeRevenda";
import { postListarBairroRevenda } from "./api/postListarBairroRevenda";
import { fetchRevendaStatus } from "../unidades/api/getRevendaStatus";
import { fetchRevendaCaracteristicaListar } from "../unidades/api/postRevendaCaracteristicaListar";
import { NumberOptions } from "../unidades/novaRevenda/utils";
import { fetchPrecoRevenda } from "./api/getRevendaPreco";
import { postRelatorioUG } from "./api/postRelatorioUG";

const today = new Date();

export const UnidadeGeralComponent = ({
  authToken,
  shouldClear,
}: {
  authToken: string;
  shouldClear: boolean;
}) => {
  const [data, setData] = useState(defaultUGData);
  const [submitting, setSubmitting] = useState(false);

  const [cidadeOptions, setCidadeOptions] = useState<any[]>([]);
  const [bairroOptions, setBairroOptions] = useState<any[]>([]);
  const [loadingCidadeBairro, setLoadingCidadeBairro] = useState(true);

  const {
    data: revendaEmpreendimentoOptions,
    isLoading: loadingEmpreendimentos,
  } = fetchRevendaEmpreendimentosListar({
    enabled: true,
    authToken,
    registros: 5000,
  });
  const torreOptions =
    revendaEmpreendimentoOptions &&
    data?.idRevendaEmpreendimento &&
    revendaEmpreendimentoOptions?.find(
      (item: any) => item?.id === data?.idRevendaEmpreendimento
    )?.revendaEmpreendimentoTorres?.length > 0
      ? revendaEmpreendimentoOptions
          ?.find((item: any) => item?.id === data?.idRevendaEmpreendimento)
          ?.revendaEmpreendimentoTorres?.map((item: any) => ({
            label: item?.descricao,
            value: item?.id,
          }))
      : [];
  const { data: areaOptions, isLoading: loadingArea } = fetchRevendaAreaUtil({
    enabled: true,
    authToken,
  });
  const { data: statusOptions, isLoading: loadingStatus } = fetchRevendaStatus({
    enabled: true,
    authToken,
  });
  const { data: caracteristicaOptions, isLoading: loadingCaracteristica } =
    fetchRevendaCaracteristicaListar({
      enabled: true,
      authToken,
    });
  const { data: precoOptions, isLoading: loadingPreco } = fetchPrecoRevenda({
    enabled: true,
    authToken,
  });
  const fetchCidadeBairro = useCallback(
    debounce((idEmpreendimento) => {
      if (!idEmpreendimento) return;

      setLoadingCidadeBairro(true);

      Promise.all([
        postListarCidadeRevenda({ authToken, idEmpreendimento }),
        postListarBairroRevenda({ authToken, idEmpreendimento }),
      ])
        .then(([resCidade, resBairro]) => {
          const {
            success: successC,
            data: dataC,
            notifications: notifC,
          } = resCidade;
          const {
            success: successB,
            data: dataB,
            notifications: notifB,
          } = resBairro;

          if (!successC || !successB) {
            const msg = [
              notifC?.[0]?.message ? `Cidade: ${notifC?.[0]?.message}` : "",
              notifB?.[0]?.message ? `Bairro: ${notifB?.[0]?.message}` : "",
            ]
              .filter(Boolean)
              .join(", ");

            if (notifC?.[0]?.message) setCidadeOptions([]);
            if (notifB?.[0]?.message) setBairroOptions([]);
            throw new Error(msg || "");
          }

          setCidadeOptions(
            dataC?.map((item: any) => ({
              label: item?.key,
              value: item?.value,
            }))
          );
          setBairroOptions(
            dataB?.map((item: any) => ({
              label: item?.key,
              value: item?.value,
            }))
          );
        })
        .catch((err) => {
          console.error("Error fetching cidade e bairro", err);
          toast.error(
            err?.message
              ? `Erro: ${err?.message}`
              : "Erro ao buscar cidade/bairro"
          );
        })
        .finally(() => setLoadingCidadeBairro(false));
    }, 750),
    []
  );

  useEffect(() => {
    handleClear();
  }, [shouldClear]);

  const handleClear = () =>
    setData((prev) => ({ ...defaultUGData, tipo: prev?.tipoRelatorio }));

  const handleDownload = ({ nome, base64, contentType }: FileUploadI) => {
    try {
      const fullBase64 = `data:${contentType};base64,${base64}`;
      const link = document.createElement("a");
      link.href = fullBase64;
      link.download = nome ?? "relatorio.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Relatório gerado com sucesso");
    } catch (err) {
      console.error("Download failed: ", err);
      toast.error("Erro ao baixar relatório");
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);

    postRelatorioUG({ authToken, ...data })
      .then(({ success, data: res, notifications }) => {
        if (!success) throw new Error(notifications?.[0]?.message ?? "");
        if (success && !res) {
          toast.info("Nenhum dado encontrado para os parâmetros informados");
          return;
        }
        handleDownload(res);
      })
      .catch((err) => {
        console.error("Error fetching equipes", err);
        toast.error(
          err?.message ? `Erro: ${err?.message}` : "Erro ao buscar equipes"
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <S.Container>
      <S.Flex>
        <RadioGroup
          aria-labelledby="radio-buttons-group-label"
          value={data?.tipoRelatorio}
          name="radio-buttons-group"
          row
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setData((prev) => ({
              ...prev,
              tipoRelatorio: e?.target?.value,
            }));
          }}
        >
          <FormControlLabel value={"U"} control={<Radio />} label="Unidade" />
          <FormControlLabel value={"G"} control={<Radio />} label="Geral" />
        </RadioGroup>
      </S.Flex>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Empreendimento"}</p>
          <SelectComponent
            data={
              revendaEmpreendimentoOptions
                ? revendaEmpreendimentoOptions?.map((item: any) => ({
                    label: item?.nome,
                    value: item?.id,
                  }))
                : []
            }
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => {
                const newData = {
                  ...prev,
                  idRevendaEmpreendimento: e?.value ?? "",
                  idRevendaEmpreendimentoTorre: "",
                };
                fetchCidadeBairro(e?.value);
                return newData;
              });
            }}
            value={
              revendaEmpreendimentoOptions
                ?.map((item: any) => ({
                  label: item?.nome,
                  value: item?.id,
                }))
                ?.find(
                  (item: any) => item?.value === data?.idRevendaEmpreendimento
                ) ?? ""
            }
            disabled={
              loadingEmpreendimentos ||
              !revendaEmpreendimentoOptions ||
              revendaEmpreendimentoOptions?.length === 0
            }
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Torre"}</p>
          <SelectComponent
            data={torreOptions}
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                idRevendaEmpreendimentoTorre: e?.value ?? "",
              }));
            }}
            value={
              torreOptions?.find(
                (item: any) =>
                  item?.value === data?.idRevendaEmpreendimentoTorre
              ) ?? ""
            }
            disabled={loadingEmpreendimentos || torreOptions?.length === 0}
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Cidade"}</p>
          <SelectComponent
            data={cidadeOptions ?? []}
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                cidade: e?.value ?? "",
              }));
            }}
            value={
              cidadeOptions?.find(
                (item: any) => item?.value === data?.cidade
              ) ?? ""
            }
            disabled={
              loadingCidadeBairro ||
              cidadeOptions?.length === 0 ||
              !cidadeOptions ||
              !data?.idRevendaEmpreendimento
            }
            borderRadius="8px"
          />
        </S.Field>
      </S.Row>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Bairro"}</p>
          <SelectComponent
            data={bairroOptions ?? []}
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                bairro: e?.value ?? "",
              }));
            }}
            value={
              bairroOptions?.find(
                (item: any) => item?.value === data?.bairro
              ) ?? ""
            }
            disabled={
              loadingCidadeBairro ||
              bairroOptions?.length === 0 ||
              !bairroOptions ||
              !data?.idRevendaEmpreendimento
            }
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Característica"}</p>
          <SelectComponent
            data={
              caracteristicaOptions
                ? caracteristicaOptions?.map((item: any) => ({
                    label: item?.nome,
                    value: item?.id,
                  }))
                : []
            }
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                idRevendaCaracteristica: e?.value ?? "",
              }));
            }}
            value={
              caracteristicaOptions
                ?.map((item: any) => ({ label: item?.nome, value: item?.id }))
                ?.find(
                  (item: any) => item?.value === data?.idRevendaCaracteristica
                ) ?? ""
            }
            disabled={
              loadingCaracteristica ||
              !caracteristicaOptions ||
              caracteristicaOptions?.length === 0
            }
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Número de suites"}</p>
          <SelectComponent
            data={NumberOptions}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                numeroSuites: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              []?.find((item: any) => item?.value === data?.numeroSuites) ?? ""
            }
            disabled={false}
            borderRadius="8px"
          />
        </S.Field>
      </S.Row>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Vagas"}</p>
          <SelectComponent
            data={NumberOptions}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                vagas: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              NumberOptions?.find((item: any) => item?.value === data?.vagas) ??
              ""
            }
            disabled={false}
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Área útil"}</p>
          <SelectComponent
            data={areaOptions ?? []}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                areaUtil: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              areaOptions?.find(
                (item: any) => item?.value === data?.areaUtil
              ) ?? ""
            }
            disabled={loadingArea || areaOptions?.length === 0}
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Preço"}</p>
          <SelectComponent
            data={precoOptions ?? []}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                preco: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              precoOptions?.find((item: any) => item?.value === data?.preco) ??
              ""
            }
            disabled={
              loadingPreco || precoOptions?.length === 0 || !precoOptions
            }
            borderRadius="8px"
          />
        </S.Field>
      </S.Row>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Status da Reserva"}</p>
          <Select
            data={statusOptions ?? []}
            onChange={(val: number[]) => {
              setData((prev) => ({
                ...prev,
                status: val,
              }));
            }}
            value={data?.status}
            multiple
            placeholder=""
            disabled={
              loadingStatus || statusOptions?.length === 0 || !statusOptions
            }
            renderLabel
          />
        </S.Field>
        <S.Row>
          <S.Field>
            <p className="label">{"Período de"}</p>
            <DatePicker
              value={data.periodoDe}
              onChange={(val: any) => {
                setData((prev) => ({
                  ...prev,
                  periodoDe: val,
                }));
              }}
              maxDate={data.periodoAte || today}
            />
          </S.Field>
          <S.Field>
            <p className="label">{"Período até"}</p>
            <DatePicker
              value={data.periodoAte}
              onChange={(val: any) => {
                setData((prev) => ({
                  ...prev,
                  periodoAte: val,
                }));
              }}
              minDate={data.periodoDe || undefined}
              maxDate={today}
            />
          </S.Field>
        </S.Row>
        <div id="dummy" />
      </S.Row>

      <S.Row>
        <div id="dummy" />
        <S.Row>
          <SecondaryButton content={"Limpar filtros"} onClick={handleClear} />
          <SecondaryButton
            content={
              submitting ? (
                <div style={{ minWidth: 100 }}>
                  <Loading size={18} color="green" />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <ExcelIcon />
                  {"Gerar Excel"}
                </div>
              )
            }
            color="success"
            style={{ padding: "0 10px" }}
            onClick={handleSubmit}
          />
        </S.Row>
      </S.Row>
    </S.Container>
  );
};
